import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useAuth } from "@/hooks/useAuth";
import {
  setupPassword,
  validateInvitationToken,
  type InvitationValidateResponse,
} from "@/api/invitations";
import {
  PasswordRequirements,
  DEFAULT_PASSWORD_POLICY,
} from "@/components/auth/PasswordRequirements";

type Stage = "loading" | "invalid" | "form" | "done";

export function SetupPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const token = searchParams.get("token") || "";

  const [stage, setStage] = useState<Stage>("loading");
  const [invite, setInvite] = useState<InvitationValidateResponse | null>(null);
  const [error, setError] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Drives the submit button — only enabled when every requirement
  // ticks green AND the confirm field matches.
  const [policyValid, setPolicyValid] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("This setup link is missing its token.");
      setStage("invalid");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const info = await validateInvitationToken(token);
        if (!cancelled) {
          setInvite(info);
          setStage("form");
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const axiosErr = err as { response?: { data?: { detail?: string } } };
        setError(axiosErr.response?.data?.detail || "This invitation link is invalid.");
        setStage("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (!/[a-zA-Z]/.test(newPassword) || !/\d/.test(newPassword)) {
      setError("Password must contain at least one letter and one number");
      return;
    }
    if (newPassword !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setIsLoading(true);
    try {
      // Backend now returns a full TokenResponse on setup-password —
      // hand the access token + user payload to AuthContext so the
      // user is logged in immediately. The refresh cookie was set by
      // the server. Brief "done" state for visual confirmation, then
      // route into the dashboard.
      const res = await setupPassword(token, newPassword);
      setSession(res.access_token, res.user);
      setStage("done");
      // Landing page lives at "/", not "/dashboard" — App.tsx uses
      // ``<Route path="/" element={<DashboardPage />}>``. A previous
      // "/dashboard" target fell through to nothing and showed a blank
      // screen after the "Logging you in" flash.
      setTimeout(() => navigate("/", { replace: true }), 1200);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || "Could not set password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Lenavio" className="h-9 w-auto mx-auto" />
          {stage === "form" && invite && (
            <>
              <h2 className="text-xl font-semibold font-heading text-text-primary mt-6">
                Welcome{invite.org_name ? ` to ${invite.org_name}` : ""}
              </h2>
              <p className="text-sm text-text-muted mt-2">
                Set a password for <span className="font-medium">{invite.email}</span> to
                activate your account.
              </p>
            </>
          )}
          {stage === "loading" && (
            <h2 className="text-xl font-semibold font-heading text-text-primary mt-6">
              Checking your invitation…
            </h2>
          )}
        </div>

        {stage === "loading" && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-text-muted" />
          </div>
        )}

        {stage === "invalid" && (
          <div className="rounded-lg bg-danger/10 border border-danger/20 px-4 py-6 text-center">
            <p className="text-sm text-danger font-medium">{error}</p>
            <p className="text-xs text-text-muted mt-3">
              Contact your HR admin for a fresh invitation link.
            </p>
            <Link
              to="/login"
              className="mt-4 inline-block text-sm text-primary hover:underline"
            >
              Go to sign in
            </Link>
          </div>
        )}

        {stage === "done" && (
          <div className="rounded-lg bg-success/10 border border-success/20 px-4 py-6 text-center">
            <CheckCircle2 className="h-10 w-10 text-success mx-auto mb-2" />
            <p className="text-sm text-text-primary font-medium">
              You&rsquo;re all set
            </p>
            <p className="text-xs text-text-muted mt-1">
              Logging you in&hellip;
            </p>
          </div>
        )}

        {stage === "form" && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="New password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              icon={<Lock className="h-4 w-4" />}
              autoComplete="new-password"
              required
            />
            <Input
              label="Confirm password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              icon={<Lock className="h-4 w-4" />}
              autoComplete="new-password"
              required
            />
            {/* Live policy checklist. Falls back to the platform
                defaults — the org's actual policy is enforced
                server-side via /auth/setup-password regardless of
                what the SPA shows. */}
            <PasswordRequirements
              password={newPassword}
              policy={DEFAULT_PASSWORD_POLICY}
              onValidityChange={setPolicyValid}
            />
            {error && (
              <div className="rounded-lg bg-danger/10 border border-danger/20 px-4 py-3">
                <p className="text-sm text-danger">{error}</p>
              </div>
            )}
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={!policyValid || newPassword !== confirm}
              className="w-full"
            >
              Activate account
            </Button>
            <Link
              to="/login"
              className="block text-center text-sm text-text-muted hover:text-text-primary"
            >
              Back to sign in
            </Link>
          </form>
        )}
      </motion.div>
    </div>
  );
}
